/**
 * Phase 2 API client.
 *
 * Thin wrappers over fetch that reuse the shared error handling in auth/api.ts,
 * so every page reports failures the same way.
 */

import type {
  ActivityPeriod,
  ActivityResponse,
  CharacterAlias,
  KarmaEntry,
  KarmaGrantResponse,
  KarmaResponse,
  LootAnnouncementOutcome,
  LootHistoryEntry,
  LootMutationResponse,
  LootQueueEntry,
  LootQueueResponse,
  MemberIdentity,
  MembersResponse,
  PeopleResponse,
  PersonSummary,
} from '@vlastiq/contracts';
import { apiRequest } from '../auth/api.ts';

/*
  The loot and member-picker types are IMPORTED from the contract package, not
  restated here.

  Everything else in this file is a hand-written copy of a server shape, which
  works only because those shapes predate `@vlastiq/contracts` carrying them. A
  second hand-written copy of a type the contract already defines is how the two
  drift: the server would answer `posted: false, reason: 'unchanged'` while this
  file still described four reasons, and the compiler would have nothing to say
  about it.

  Re-exported so pages keep importing every type from `data/api.ts`, the one
  import they already have — a page importing half its types from here and half
  from the contract package reads as though the two halves came from different
  endpoints.
*/
export type {
  ActivityPeriod,
  ActivityResponse,
  CharacterAlias,
  KarmaEntry,
  KarmaGrantResponse,
  KarmaResponse,
  LootAnnouncementOutcome,
  LootHistoryEntry,
  LootMutationResponse,
  LootQueueEntry,
  LootQueueResponse,
  MemberIdentity,
  MembersResponse,
  PeopleResponse,
  PersonSummary,
};

export type AttributeKind = 'character' | 'timeseries';

export type AttributeValueType =
  | 'number'
  | 'text'
  | 'boolean'
  | 'date'
  | 'single_select'
  | 'multi_select';

export interface AttributeDef {
  id: number;
  kind: AttributeKind;
  key: string;
  label: string;
  description: string | null;
  valueType: AttributeValueType;
  options: string[] | null;
  unit: string | null;
  sortOrder: number;
  /**
   * Only an admin may WRITE this one's values; everybody still reads them.
   *
   * The member page renders these fields disabled rather than hiding them — a
   * rank an officer assigns is information about you, and a field that vanishes
   * looks like data that was lost. It also excludes them from what it SENDS,
   * because the server refuses the whole save if one is present.
   */
  adminOnly: boolean;
  isActive: boolean;
}

export interface AttributeValue {
  id: number;
  characterId: number;
  defId: number;
  periodStart: string | null;
  value: string | number | boolean | string[] | null;
  valueNumeric: number | null;
  updatedAt: string;
}

export interface Character {
  id: number;
  /**
   * ⚠️ NULLABLE (DECISION-33). An admin can create a character for a player who
   * does not use Telegram, and nobody owns it — so every list that renders an
   * owner has to survive there being none.
   *
   * Comparing it against the viewer's own id still does the right thing:
   * `null` matches nobody, which is exactly the answer wanted.
   */
  memberId: number | null;
  name: string;
}

export interface RosterEntry {
  character: Character;
  memberUsername: string | null;
  memberFirstName: string | null;
  memberLastName: string | null;
  /**
   * Current attribute values, keyed by the definition's `key`.
   *
   * Every key is optional: the catalogue is admin-seeded (DECISION-9) and
   * ships empty, so a missing key means either "no such attribute exists here"
   * or "nobody has filled it in". The UI cannot tell those apart and must not
   * try — both render as «—».
   */
  values: Record<string, string | number | boolean | string[] | null>;
  /** Karma received, all time (DECISION-37). Zero for everybody by default. */
  karma: number;
  /**
   * How many points the VIEWER may still give this character today.
   *
   * ⚠️ ZERO ALSO MEANS «THIS IS YOU», and the two must be told apart before a
   * sentence is written. `character.memberId` is what distinguishes them: «на
   * сегодня всё» on your own row would promise something that never arrives.
   */
  karmaRemainingToday: number;
}

export type EventKind = 'daily' | 'weekly' | 'once';

/** Whose event it is: the game runs it on a timer, or the clan organises it. */
export type EventCategory = 'game' | 'clan';

export interface ScheduledEvent {
  id: number;
  title: string;
  /** The note the bot works from when it writes the reminder. */
  description: string | null;
  kind: EventKind;
  category: EventCategory;
  weekday: number | null;
  minutesOfDay: number | null;
  startsAt: string | null;
  durationMinutes: number | null;
  /** Whether an admin records who turned up to each occurrence. */
  tracksAttendance: boolean;
  /** Whether this event advances the loot rotation. */
  tracksLoot: boolean;
  isActive: boolean;
}

export interface Occurrence {
  eventId: number;
  title: string;
  description: string | null;
  kind: EventKind;
  category: EventCategory;
  durationMinutes: number | null;
  at: string;
}

/* ------------------------------------------------------------- attendance */

/**
 * One instance of an event that actually happened.
 *
 * `attendanceRecordedAt === null` means NOBODY HAS FILLED IT IN — which is not
 * the same as everybody being absent, and the UI must not render it as such.
 */
export interface EventOccurrence {
  id: number;
  eventId: number;
  occurrenceAt: string;
  attendanceRecordedAt: string | null;
  attendanceRecordedByUserId: number | null;
  /**
   * @deprecated RETIRED BY DECISION-33, and still on the wire because of it.
   *
   * The column survives — dropping it would mean rebuilding a table two others
   * reference — and the occurrence row is returned whole, so the field arrives
   * on every response and is ALWAYS null. It is kept in the type rather than
   * deleted so that anyone who finds it in a network response can find the
   * reason here instead of assuming the client is dropping a value.
   *
   * Whose turn it is now lives in the loot QUEUE, which no occurrence claims:
   * announcing an event used to spend somebody's turn whether or not they
   * turned up. Nothing may read this.
   */
  lootCharacterId: number | null;
  note: string | null;
}

export interface OccurrenceRow {
  occurrence: EventOccurrence;
  event: ScheduledEvent;
  presentCount: number;
  absentCount: number;
}

export interface AttendanceEntry {
  characterId: number;
  characterName: string;
  /**
   * ⚠️ NULLABLE since DECISION-33, and the type said `number` for a while.
   *
   * An admin-created character has no Telegram owner. The lie matters because
   * of what it invites: `entry.memberId === ownMemberId` would typecheck with
   * no `!== null` guard, and both sides are null while `/api/me` resolves — so
   * every unowned character would render as «вы». `LootQueue.isOwnRow` and
   * `DashboardPage.isOwnCharacter` both carry that guard for exactly this.
   */
  memberId: number | null;
  /** Null when no verdict was recorded for this character at this occurrence. */
  status: 'present' | 'absent' | null;
}

export interface AttendanceSummaryRow {
  characterId: number;
  characterName: string;
  /** Nullable for an admin-created character — see `AttendanceEntry.memberId`. */
  memberId: number | null;
  memberUsername: string | null;
  memberFirstName: string | null;
  present: number;
  absent: number;
  /** null when nothing was recorded for them in the window. */
  rate: number | null;
}

export interface KnowledgeItem {
  id: number;
  title: string;
  content: string;
  isActive: boolean;
  sortOrder: number;
  /**
   * Who wrote it. `manual` is an admin's instruction and is ALWAYS sent to the
   * model; `automatic` is something the bot inferred and is sampled when the
   * prompt budget runs out.
   */
  source: 'manual' | 'automatic';
  createdByUserId: number | null;
  authorUsername: string | null;
  authorFirstName: string | null;
  /**
   * WHO the item is about, when it names one person (DECISION-34).
   *
   * ⚠️ NOT `createdByUserId`, and routinely a different person: Дмитрий asking
   * the bot to remember something about Лёха writes one row naming both. The
   * author answers "who told the bot this"; the subject answers "who is this
   * about", and only the second decides whether the item reaches the prompt
   * when Лёха speaks.
   *
   * Null — the common case — means "about the group, or about nothing in
   * particular".
   */
  subjectCharacterId: number | null;
  /** The subject's CHARACTER name — the only one an unowned character has. */
  subjectCharacterName: string | null;
  subjectUsername: string | null;
  subjectFirstName: string | null;
  subjectLastName: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ------------------------------------------------------------------ member */

export const fetchCatalogue = () =>
  apiRequest<{ character: AttributeDef[]; timeseries: AttributeDef[] }>('/api/attributes');

export const fetchMyCharacter = () =>
  apiRequest<{ character: Character | null; values: AttributeValue[] }>('/api/me/character');

export const saveMyCharacterName = (name: string) =>
  apiRequest<{ character: Character }>('/api/me/character', {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });

export const saveMyAttributes = (values: Record<string, unknown>) =>
  apiRequest<{ written: number; values: AttributeValue[] }>('/api/me/character/attributes', {
    method: 'PUT',
    body: JSON.stringify({ values }),
  });

export const fetchRoster = () => apiRequest<{ characters: RosterEntry[] }>('/api/roster');

/**
 * The rotation, in the order the server holds it (DECISION-33).
 *
 * Readable by every member — the queue is the answer to "чья очередь", which is
 * a question everyone asks and only an admin answers.
 *
 * ⚠️ THE ARRAY ORDER IS THE DATA. `position` is stored, not derived, so a client
 * that sorts this by anything at all overrules a decision somebody made by hand,
 * which is the entire point of the rework.
 */
export const fetchLootQueue = () => apiRequest<LootQueueResponse>('/api/loot/queue');

export const fetchSchedule = (days = 14) =>
  apiRequest<{ occurrences: Occurrence[] }>(`/api/schedule?days=${days}`);

export const fetchTimeseries = (characterId: number, weeks = 12) =>
  apiRequest<{ weeks: string[]; entries: AttributeValue[]; definitions: AttributeDef[] }>(
    `/api/characters/${characterId}/timeseries?weeks=${weeks}`,
  );

export const fetchKnowledge = () => apiRequest<{ items: KnowledgeItem[] }>('/api/knowledge');

/**
 * One character's karma, with who gave it and why (DECISION-37).
 *
 * Fetched when the modal OPENS, never on mount: the home page draws every row's
 * total from `/api/roster`, and a history nobody has asked to see is a request
 * per character on a page that already makes five.
 */
export const fetchKarma = (characterId: number) =>
  apiRequest<KarmaResponse>(`/api/karma/${characterId}`);

/**
 * Give one point.
 *
 * ⚠️ THE REFUSALS ARRIVE AS 409s WITH THE SERVER'S OWN SENTENCE, and `errorText`
 * is what puts that sentence on screen. The three reasons — себе нельзя, на
 * сегодня всё, персонажа больше нет — need different next steps, so the client
 * deliberately does not compose its own message for them.
 */
export const giveKarma = (characterId: number, message: string | null) =>
  apiRequest<KarmaGrantResponse>('/api/karma', {
    method: 'POST',
    body: JSON.stringify({ characterId, message }),
  });

/**
 * Remove one karma entry. Admin-only.
 *
 * ⚠️ NOT A DOWNVOTE, AND THERE IS NO ENDPOINT THAT IS ONE. Nobody can lower
 * anybody's karma; this deletes an entry that should not exist — a bot grant
 * that landed on the wrong person, or something said in anger — and the total
 * falls because the row is gone.
 */
export const adminDeleteKarma = (id: number) =>
  apiRequest<{ ok: true }>(`/api/admin/karma/${id}`, { method: 'DELETE' });

/**
 * Who has been talking, as shares of the whole (home page, bottom card).
 *
 * ⚠️ THE RESPONSE CARRIES NO COUNTS — not per person, not in total. That is a
 * deliberate property of the endpoint rather than of the UI reading it: the
 * question is relative, and a raw count on a page every member can see invites
 * a comparison nobody asked for. See `ActivityResponse` in the contract.
 */
export const fetchActivity = (period: ActivityPeriod) =>
  apiRequest<ActivityResponse>(`/api/activity?period=${period}`);

/* ------------------------------------------------------------------- admin */

export const adminFetchDefs = () =>
  apiRequest<{ definitions: AttributeDef[] }>('/api/admin/attribute-defs');

export const adminCreateDef = (input: Partial<AttributeDef>) =>
  apiRequest<{ definition: AttributeDef }>('/api/admin/attribute-defs', {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const adminUpdateDef = (id: number, patch: Partial<AttributeDef>) =>
  apiRequest<{ definition: AttributeDef }>(`/api/admin/attribute-defs/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

/**
 * One character with its attribute values, for the admin editor.
 *
 * Same shape as `fetchMyCharacter`, and deliberately so — the editor on the
 * rotation page renders the same `AttributeField`s as Мой персонаж, because a
 * character an admin types for somebody with no Telegram is not a lesser kind
 * of character.
 */
export const adminFetchCharacter = (characterId: number) =>
  apiRequest<{ character: Character; values: AttributeValue[] }>(
    `/api/admin/characters/${characterId}`,
  );

export const adminSaveCharacterAttributes = (characterId: number, values: Record<string, unknown>) =>
  apiRequest<{ written: number }>(`/api/admin/characters/${characterId}/attributes`, {
    method: 'PUT',
    body: JSON.stringify({ values }),
  });

export const adminSaveTimeseries = (
  characterId: number,
  values: Record<string, unknown>,
  weekOf: string,
) =>
  apiRequest<{ written: number; periodStart: string }>(
    `/api/admin/characters/${characterId}/timeseries`,
    { method: 'PUT', body: JSON.stringify({ values, weekOf }) },
  );

/* --------------------------------------------------------- loot (admin) --- */

/*
  The five queue writes and the announcement.

  All six answer with the WHOLE new queue plus what the bot did about it, so a
  caller never has to re-fetch to redraw and can never render a queue from
  before its own write. The announcement half is the reason they do not answer
  `{ ok: true }`: most of `posted: false`'s reasons are the feature correctly
  staying quiet, and a caller that cannot see them has no way to say so.
*/

const lootWrite = (path: string, body?: Record<string, unknown>) =>
  apiRequest<LootMutationResponse>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

/*
  ⚠️ THE THREE WRITES BELOW MOVE THE QUEUE AND SAY NOTHING TO THE GROUP
  (DECISION-43). None of them records history either — moving the queue is a
  PLAN, and «Объявить очередь» is how a plan is published. `move-to-front` used
  to announce, gated on a loot event being inside 24 hours; dragging a row to the
  top is the same act, and an app where the consequence depends on which control
  you reached for is worse than one that never posts.
*/

/** Straight to position 1; everybody else shifts down one. */
export const adminMoveLootToFront = (characterId: number) =>
  lootWrite('/api/admin/loot/move-to-front', { characterId });

/**
 * Straight to the back; everybody behind them shifts up one.
 *
 * ⚠️ NOT «не явился» WITH THE LOGGING TURNED OFF — it is the absence of that
 * claim. The same movement was previously reachable only by asserting that
 * somebody took the drop or missed their turn, both permanent and both counted
 * on every screen.
 */
export const adminMoveLootToEnd = (characterId: number) =>
  lootWrite('/api/admin/loot/move-to-end', { characterId });

/**
 * Submit a whole new order — the write behind dragging a row.
 *
 * ⚠️ IT SENDS THE ENTIRE QUEUE, AND THE SERVER REFUSES ANYTHING THAT IS NOT AN
 * EXACT PERMUTATION OF WHAT IT HOLDS. That is the only loot call that can 409:
 * a second admin adding or deleting a character while this page was open makes
 * the array describe a queue that no longer exists, and the answer is to reload
 * rather than to write half of it.
 */
export const adminReorderLootQueue = (order: number[]) =>
  lootWrite('/api/admin/loot/reorder', { order });

/** They took the drop: logged, and to the back of the queue. */
export const adminRecordLootCollected = (characterId: number) =>
  lootWrite('/api/admin/loot/collected', { characterId });

/**
 * Their turn came up and they were not there: logged, and to the back.
 *
 * The same movement as «собрал лут» and a different history row, deliberately —
 * "has never taken any" and "came up four times and missed all four" are
 * opposite facts about a person that a queue position cannot tell apart.
 */
export const adminRecordLootSkipped = (characterId: number) =>
  lootWrite('/api/admin/loot/skipped', { characterId });

/**
 * Say the queue out loud in the group, now. Changes nothing.
 *
 * ⚠️ SINCE DECISION-43 THIS IS THE ONLY WAY AN ORDER SOMEBODY ARRANGED BY HAND
 * REACHES THE GROUP. Reordering is silent, so this button is not a convenience
 * beside an automatic message — it IS the message.
 *
 * Takes no body: `trigger: 'manual'` is the server's business, and it is never
 * gated on an event being imminent — a person pressing a button is the opposite
 * of unattended.
 */
export const adminAnnounceLootQueue = () => lootWrite('/api/admin/loot/announce');

/* ---------------------------------------------------- characters (admin) --- */

/**
 * Create a character with no Telegram owner (DECISION-33).
 *
 * For the players in this clan who are not on Telegram: they still take their
 * turn, and before this the only way to represent them was not to. Nothing can
 * ever sign in as one — there is no user id on the row to authenticate.
 */
export const adminCreateCharacter = (name: string) =>
  apiRequest<{ character: Character }>('/api/admin/characters', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });

export const adminRenameCharacter = (id: number, name: string) =>
  apiRequest<{ character: Character }>(`/api/admin/characters/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ name }),
  });

/**
 * ⚠️ THE SERVER DELETES UNOWNED CHARACTERS ONLY, and the UI must not offer this
 * for any other row. Deleting a character cascades to its attribute values, its
 * whole attendance history and its place in the rotation — for an owned one that
 * is a member losing everything they ever entered, on somebody else's click.
 */
export const adminDeleteCharacter = (id: number) =>
  apiRequest<{ ok: true }>(`/api/admin/characters/${id}`, { method: 'DELETE' });

/**
 * Everyone this app has ever seen, for the "who is this about" picker
 * (DECISION-34).
 *
 * Not the current group: `members` is a profile cache, so somebody who has left
 * is still in this list. That is what makes it usable for attributing an old
 * note, and it is why it must never be read as an authorisation list.
 */
export const adminFetchMembers = () => apiRequest<MembersResponse>('/api/admin/members');

/**
 * Everybody in the clan, as Админка → Состав lists them (DECISION-36).
 *
 * ⚠️ NOT `fetchRoster` UNDER ANOTHER NAME. A roster row is a CHARACTER; a person
 * here may be a character with no owner, or a member who never made a character
 * — and an admin cannot type an alias against a row that is not on the screen.
 */
export const adminFetchPeople = () => apiRequest<PeopleResponse>('/api/admin/people');

/**
 * Teach the bot a name the group uses for somebody.
 *
 * ⚠️ A COLLISION IS A 400 NAMING THE CURRENT HOLDER, and that sentence is the
 * feature. An alias two people share resolves to nobody — the state it was typed
 * to fix — so the server refuses it rather than storing a row that silently does
 * nothing. `errorText` renders the server's message; do not replace it.
 */
export const adminAddAlias = (characterId: number, alias: string) =>
  apiRequest<{ alias: CharacterAlias }>(`/api/admin/characters/${characterId}/aliases`, {
    method: 'POST',
    body: JSON.stringify({ alias }),
  });

export const adminDeleteAlias = (id: number) =>
  apiRequest<{ ok: true }>(`/api/admin/aliases/${id}`, { method: 'DELETE' });

export const adminFetchKnowledge = () =>
  apiRequest<{ items: KnowledgeItem[] }>('/api/admin/knowledge');

export const adminCreateKnowledge = (title: string, content: string) =>
  apiRequest<{ item: KnowledgeItem }>('/api/admin/knowledge', {
    method: 'POST',
    body: JSON.stringify({ title, content }),
  });

export const adminUpdateKnowledge = (id: number, patch: Partial<KnowledgeItem>) =>
  apiRequest<{ item: KnowledgeItem }>(`/api/admin/knowledge/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

export const adminDeleteKnowledge = (id: number) =>
  apiRequest<{ ok: true }>(`/api/admin/knowledge/${id}`, { method: 'DELETE' });

export const adminFetchSchedule = () =>
  apiRequest<{ events: ScheduledEvent[] }>('/api/admin/schedule');

/**
 * Post today's morning brief now (DECISION-27).
 *
 * Deliberately ignores the once-a-day claim the scheduler keeps: every reason
 * to press the button is a day whose claim is already taken.
 */
export const adminSendDailyBrief = () =>
  apiRequest<{ ok: true }>('/api/admin/schedule/brief', { method: 'POST' });

/**
 * What came of asking CORE-07 to say something unprompted (DECISION-31).
 *
 * ⚠️ `withheld` IS A SUCCESSFUL REQUEST. The bot declining to make a dry remark
 * into a pile-on or somebody's bad news is the feature working, so it arrives
 * as a 200 with a reason rather than as an error — and the UI must not render
 * it as one.
 */
export type ChatterOutcome =
  | { posted: true }
  | { posted: false; reason: 'withheld'; situation: string[] }
  | { posted: false; reason: 'generation_failed' };

/**
 * Post one unprompted small-talk message now (DECISION-31).
 *
 * Ignores the two-hour slot, the 08:00-22:00 window and the activity threshold
 * — those bound what the bot does UNATTENDED. It also works while
 * BOT_CHAT_ENABLED is false, which is how you see what it says before letting
 * the clock send eight a day.
 */
export const adminSendChatterNow = () =>
  apiRequest<ChatterOutcome>('/api/admin/bot/chatter', { method: 'POST' });

/**
 * Post the pinnable «Открыть NN» message to the group (then pin it by hand).
 *
 * The only route a Mini App has into a group chat: a `url` button carrying a
 * `t.me` direct link. `web_app` buttons — the one-tap kind — are private-chat
 * only, per the Bot API.
 */
/**
 * The direct link the group button will carry.
 *
 * Shown on the admin page so it can be tested before it is posted — whether it
 * opens anything depends on @BotFather, which this app cannot check.
 */
export const adminFetchAppLink = () =>
  apiRequest<{ link: string; usesShortName: boolean }>('/api/admin/bot/app-link');

export const adminPostAppButton = () =>
  apiRequest<{ ok: true }>('/api/admin/bot/app-button', { method: 'POST' });

export const adminCreateEvent = (input: Partial<ScheduledEvent>) =>
  apiRequest<{ event: ScheduledEvent }>('/api/admin/schedule', {
    method: 'POST',
    body: JSON.stringify(input),
  });

/* -------------------------------------------------------- attendance (read) */

const range = (from: Date, to: Date) =>
  `from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`;

/** Recorded occurrences of one event. History, not a calendar. */
export const fetchOccurrences = (eventId: number, from: Date, to: Date) =>
  apiRequest<{ occurrences: OccurrenceRow[] }>(
    `/api/events/${eventId}/occurrences?${range(from, to)}`,
  );

export const fetchOccurrenceAttendance = (occurrenceId: number) =>
  apiRequest<{ occurrence: EventOccurrence; attendance: AttendanceEntry[] }>(
    `/api/occurrences/${occurrenceId}/attendance`,
  );

export const fetchAttendanceSummary = (from: Date, to: Date, eventId?: number) =>
  apiRequest<{
    from: string;
    to: string;
    eventId: number | null;
    summary: AttendanceSummaryRow[];
    occurrences: OccurrenceRow[];
  }>(`/api/attendance/summary?${range(from, to)}${eventId ? `&eventId=${eventId}` : ''}`);

/* ------------------------------------------------------- attendance (write) */

/** Idempotent: the same instant always yields the same occurrence. */
export const adminEnsureOccurrence = (eventId: number, occurrenceAt: Date) =>
  apiRequest<{ occurrence: EventOccurrence }>(`/api/admin/events/${eventId}/occurrences`, {
    method: 'POST',
    body: JSON.stringify({ occurrenceAt: occurrenceAt.toISOString() }),
  });

/**
 * Move a recorded session to another instant — the attendance moves with it.
 *
 * A 409 means another occurrence of the same event already sits there; the
 * server's message says so, and the caller shows it rather than merging.
 */
export const adminRescheduleOccurrence = (occurrenceId: number, occurrenceAt: Date) =>
  apiRequest<{ occurrence: EventOccurrence }>(`/api/admin/occurrences/${occurrenceId}`, {
    method: 'PATCH',
    body: JSON.stringify({ occurrenceAt: occurrenceAt.toISOString() }),
  });

/** Deletes the session AND everything ticked against it. No undo. */
export const adminDeleteOccurrence = (occurrenceId: number) =>
  apiRequest<{ ok: true }>(`/api/admin/occurrences/${occurrenceId}`, { method: 'DELETE' });

/**
 * Record who was there.
 *
 * Only the present ids are sent; the server marks everyone else on the roster
 * absent, so a half-filled form cannot mean "the rest were absent" by accident.
 */
export const adminRecordAttendance = (occurrenceId: number, presentCharacterIds: number[]) =>
  apiRequest<{ ok: true; rosterSize: number; attendance: AttendanceEntry[] }>(
    `/api/admin/occurrences/${occurrenceId}/attendance`,
    { method: 'PUT', body: JSON.stringify({ presentCharacterIds }) },
  );

export const adminUpdateEvent = (id: number, patch: Partial<ScheduledEvent>) =>
  apiRequest<{ event: ScheduledEvent }>(`/api/admin/schedule/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

export const adminDeleteEvent = (id: number) =>
  apiRequest<{ ok: true }>(`/api/admin/schedule/${id}`, { method: 'DELETE' });

export interface AiUsageSummary {
  provider: string;
  model: string;
  calls: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number;
  /** Dispatched calls this app could put no price on at all. */
  unpricedCalls: number;
  failures: number;
}

export interface AiSpend {
  limits: {
    maxSpendPerDayUsd: number;
    maxCostPerCallUsd: number;
    maxPromptPriceUsdPerMtok: number;
    maxCompletionPriceUsdPerMtok: number;
  };
  last24h: {
    vendorCalls: number;
    dispatchedTotal: number;
    vendorCostUsd: number;
    totalCostUsd: number;
    /** ⚠️ If non-zero, totalCostUsd is a LOWER BOUND — see DECISION-22. */
    unpricedCalls: number;
  };
}

export interface AiUsageDay {
  day: string;
  calls: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  /** Calls that reached a provider — the denominator for a per-call average. */
  dispatched: number;
  costUsd: number;
  failures: number;
}

/**
 * One ATTEMPT, not one reply.
 *
 * A single @-mention can produce several of these when a provider chain is
 * configured (DECISION-20) — `requestId` is what groups them back together.
 */
export interface AiUsageRow {
  id: number;
  at: string;
  provider: string;
  model: string;
  kind: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number;
  succeeded: boolean;
  /**
   * Either a provider failure kind, or one of `undispatchedKinds` when this
   * app refused before calling anyone. The two read very differently and the
   * table colours them differently for exactly that reason.
   */
  errorKind: string | null;
  requestId: string | null;
  attemptIndex: number | null;
  detail: string | null;
  costEstimatedUsd: number;
  /** ⚠️ False means UNPRICED, not free — the budget cannot see this call. */
  costReported: boolean;
}

export const adminFetchAiUsage = (days = 30) =>
  apiRequest<{
    since: string;
    summary: AiUsageSummary[];
    byDay: AiUsageDay[];
    recent: AiUsageRow[];
    replies: number;
    undispatchedKinds: string[];
    spend: AiSpend;
  }>(`/api/admin/ai-usage?days=${days}`);
